import React from 'react';
import _ from 'underscore';

import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Subspace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';

import { RouterError } from '../../../../app/spaces/RouterHelpers/RouterError';
import { Messages } from '../../../../codegen/Localize';
import { Omit } from '../../../../ts/helpers/TypescriptHelpers';
import { IUserSettings } from '../../../../ts/models/IUserSettings';
import { UserSettingsSubspace } from '../../../../userSettings/components/UserSettingsSubspace';
import { ServerUsers } from '../../../services/resources/ServerUsers';
import { IServerUserInfo } from '../../models/IServerUserInfo';
import { ISiteUserInfo } from '../../models/ISiteUserInfo';

export function siteUserSettingsSubspace(params: Omit<SiteUserSettingsContentOwnProps, 'renderSubspace'>): Subspace {
  return {
    name: 'settings',
    tabLabel: Messages.settings(),
    shouldRenderTabContentCount: false,
    renderContent: () => (
      <SiteUserSettingsContent {...{...params}} />
    )
  };
}

export interface SiteUserSettingsContentOwnProps {
  readonly isSessionUserSelf: boolean;
  readonly userSettings: IUserSettings;
  readonly siteUser: ISiteUserInfo;
  readonly urls: SpacesSiteUrls;
}

export type SiteUserSettingsContentProps = SiteUserSettingsContentOwnProps;

export interface SiteUserSettingsContentState {
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly serverUser: IServerUserInfo;
  readonly userRefreshTokenCount: number;
}

export class SiteUserSettingsContentComponent extends React.PureComponent<SiteUserSettingsContentProps, SiteUserSettingsContentState> {

  private unmounted: boolean = false;

  public constructor(props: SiteUserSettingsContentProps) {
    super(props);
    this.state = {
      renderChildren: false,
      hasError: _.isUndefined(this.props.userSettings),
      serverUser: undefined,
      userRefreshTokenCount: undefined
    };
  }

  public render() {
    if (this.state.renderChildren && this.haveAllDataNeededToRenderNonError()) {
      return this.renderSubSpace();
    } else if (this.state.hasError) {
      return <RouterError errorType={'siteuser'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount() {
    if (!_.isUndefined(this.props.userSettings)) {
      this.getServerUser();
      this.getUserRefreshTokenCount();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!this.haveAllDataNeededToRenderNonError()) {
      return;
    }

    if (!this.state.renderChildren) {
      this.setState({renderChildren: true});
    }
  }

  private updateState<K extends keyof SiteUserSettingsContentState>(newState: Pick<SiteUserSettingsContentState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getServerUser = async () => {
    try {
      const serverUser = await ServerUsers.instance.getUserByUsernameAndDomain(this.props.siteUser.username, this.props.siteUser.domainName);
      this.updateState({serverUser});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getUserRefreshTokenCount = async () => {
    try {
      const userRefreshTokenCount = await ServerUsers.instance.getUserRefreshTokenCount(this.props.siteUser.username, this.props.siteUser.domainName);
      this.updateState({userRefreshTokenCount});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private haveAllDataNeededToRenderNonError() {
    return !this.state.hasError && !_.isUndefined(this.state.serverUser) && !_.isUndefined(this.state.userRefreshTokenCount);
  }

  private renderSubSpace(): JSX.Element {
    return (<UserSettingsSubspace
            userSettings={this.props.userSettings}
            userRefreshTokenCount={this.state.userRefreshTokenCount}
            isSessionUserSelf={this.props.isSessionUserSelf}
            serverUser={this.state.serverUser}
            siteUser={this.props.siteUser}
            isServerUserSubplace={false}/>);
  }
}



const SiteUserSettingsContent: React.ComponentType<SiteUserSettingsContentOwnProps> = SiteUserSettingsContentComponent;
